import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Select from 'react-select';
import { FaFilter, FaTimes } from 'react-icons/fa';
import { useSelector } from 'react-redux';

const StatusSummaryChart = () => {
  const pid = useSelector(state => state.user.projectId);
  
  // Chart data state
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Filter states
  const [orgOptions, setOrgOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [schemeOptions, setSchemeOptions] = useState([]);
  const [targetGroupOptions, setTargetGroupOptions] = useState([]);
  
  // Selected filters state
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [selectedTargetGroup, setSelectedTargetGroup] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [weeksPerPage, setWeeksPerPage] = useState(30);

  // Fetch chart data
  useEffect(() => {
    const fetchChartData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('pid', pid);
        
        // Add selected filters to params if they exist
        if (selectedOrg) params.append('Organization_Name', selectedOrg.value);
        if (selectedDistrict) params.append('CitizenDistrict', selectedDistrict.value);
        if (selectedScheme) params.append('SchemeName', selectedScheme.value);
        if (selectedTargetGroup) params.append('target_group', selectedTargetGroup.value);
        
        const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/status/week-wise/?${params}`);
        const data = await response.json();
        setChartData(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching chart data:', error);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchChartData();
  }, [pid, selectedOrg, selectedDistrict, selectedScheme, selectedTargetGroup]);

  // Fetch organization options
  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const params = new URLSearchParams();
        params.append('pid', pid);
        
        // Add selected filters to params if they exist
        if (selectedOrg) params.append('Organization_Name', selectedOrg.value);
        if (selectedDistrict) params.append('CitizenDistrict', selectedDistrict.value);
        if (selectedScheme) params.append('SchemeName', selectedScheme.value);
        if (selectedTargetGroup) params.append('target_group', selectedTargetGroup.value);

        const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/status/unique-orgs/?${params}`);
        const data = await response.json();
        setOrgOptions((data.orgs || []).map(org => ({ value: org, label: org })));
      } catch (error) {
        console.error('Error fetching organizations:', error);
        setOrgOptions([]);
      }
    };
    fetchOrgs();
  }, [pid, selectedOrg, selectedDistrict, selectedScheme, selectedTargetGroup]);

  // Fetch district options
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const params = new URLSearchParams();
        params.append('pid', pid);
        
        // Add selected filters to params if they exist
        if (selectedOrg) params.append('Organization_Name', selectedOrg.value);
        if (selectedDistrict) params.append('CitizenDistrict', selectedDistrict.value);
        if (selectedScheme) params.append('SchemeName', selectedScheme.value);
        if (selectedTargetGroup) params.append('target_group', selectedTargetGroup.value);

        const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/status/unique-districts/?${params}`);
        const data = await response.json();
        setDistrictOptions((data.districts || []).map(d => ({ value: d, label: d })));
      } catch (error) {
        console.error('Error fetching districts:', error);
        setDistrictOptions([]);
      }
    };
    fetchDistricts();
  }, [pid, selectedOrg, selectedDistrict, selectedScheme, selectedTargetGroup]);

  // Fetch scheme options
  useEffect(() => {
    const fetchSchemes = async () => {
      try {
        const params = new URLSearchParams();
        params.append('pid', pid);
        
        // Add selected filters to params if they exist
        if (selectedOrg) params.append('Organization_Name', selectedOrg.value);
        if (selectedDistrict) params.append('CitizenDistrict', selectedDistrict.value);
        if (selectedScheme) params.append('SchemeName', selectedScheme.value);
        if (selectedTargetGroup) params.append('target_group', selectedTargetGroup.value);

        const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/status/unique-schemes/?${params}`);
        const data = await response.json();
        setSchemeOptions((data.schemes || []).map(s => ({ value: s, label: s })));
      } catch (error) {
        console.error('Error fetching schemes:', error);
        setSchemeOptions([]);
      }
    };
    fetchSchemes();
  }, [pid, selectedOrg, selectedDistrict, selectedScheme, selectedTargetGroup]);

  // Fetch target group options
  useEffect(() => {
    const fetchTargetGroups = async () => {
      try {
        const params = new URLSearchParams();
        params.append('pid', pid);
        
        // Add selected filters to params if they exist
        if (selectedOrg) params.append('Organization_Name', selectedOrg.value);
        if (selectedDistrict) params.append('CitizenDistrict', selectedDistrict.value);
        if (selectedScheme) params.append('SchemeName', selectedScheme.value);
        if (selectedTargetGroup) params.append('target_group', selectedTargetGroup.value);

        const response = await fetch(`https://api-project360-insights.haqdarshak.com/kpi/status/unique-tg/?${params}`);
        const data = await response.json();
        setTargetGroupOptions((data.tg || []).map(g => ({ value: g, label: g })));
      } catch (error) {
        console.error('Error fetching target groups:', error);
        setTargetGroupOptions([]);
      }
    };
    fetchTargetGroups();
  }, [pid, selectedOrg, selectedDistrict, selectedScheme, selectedTargetGroup]);

  // Clear all filters
  const clearFilters = () => {
    setSelectedOrg(null);
    setSelectedDistrict(null);
    setSelectedScheme(null);
    setSelectedTargetGroup(null);
    setCurrentPage(0);
  };

  // Pagination calculations
  const totalPages = Math.ceil(chartData.length / weeksPerPage);
  const paginatedData = chartData.slice(
    currentPage * weeksPerPage,
    (currentPage + 1) * weeksPerPage
  ).map(item => ({
    week: `Week ${item.week_number} (${item.week_start_date})`,
    brValue: item.total_br_value,
    startDate: item.week_start_date,
    endDate: item.week_end_date
  }));

  const dynamicBarSize = Math.max(8, Math.min(60, Math.floor(1000 / (paginatedData.length || 1))));

  return (
    <div className="bg-white rounded-xl shadow-sm shadow-black border px-2 py-1 ml-[4vw] max-w-[90vw] mb-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-pink-700">Week-Wise BR Achievement</h2>
        <div className="flex items-center space-x-2">
          <FaFilter className="text-gray-500" />
          <span className="text-sm text-gray-600">Filters</span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-1">
        <Select
          className="text-sm"
          isClearable
          placeholder="All Organizations"
          value={selectedOrg}
          onChange={setSelectedOrg}
          options={orgOptions}
        />
        <Select
          className="text-sm"
          isClearable
          placeholder="All Districts"
          value={selectedDistrict}
          onChange={setSelectedDistrict}
          options={districtOptions}
        />
        <Select
          className="text-sm"
          isClearable
          placeholder="All Schemes"
          value={selectedScheme}
          onChange={setSelectedScheme}
          options={schemeOptions}
        />
        <Select
          className="text-sm"
          isClearable
          placeholder="All Target Groups"
          value={selectedTargetGroup}
          onChange={setSelectedTargetGroup}
          options={targetGroupOptions}
        />
        <button
          className="bg-red-100 text-red-700 text-sm font-semibold p-2 rounded flex items-center justify-center space-x-1 hover:bg-red-200 transition-colors"
          onClick={clearFilters}
        >
          <FaTimes />
          <span>Clear Filters</span>
        </button>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center space-x-2">
          <label htmlFor="weeks-per-page" className="text-sm text-gray-600">Weeks per page:</label>
          <select
            id="weeks-per-page"
            value={weeksPerPage}
            onChange={(e) => {
              setWeeksPerPage(Number(e.target.value));
              setCurrentPage(0);
            }}
            className="border border-gray-300 px-4 py-1 rounded text-sm"
          >
            {[10, 20, 30, 50, 100, 200, 400].map(value => (
              <option key={value} value={value}>{value}</option>
            ))}
          </select>
        </div>
        <div>
          <span>
            <span className="text-sm font-bold text-purple-900">
            Showing weeks{' '}
            {currentPage * weeksPerPage + 1}-
            {Math.min((currentPage + 1) * weeksPerPage, chartData.length)} of {chartData.length}
          </span>
          </span>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
            disabled={currentPage === 0}
            className={`px-4 py-1 rounded-md text-sm ${
              currentPage === 0 
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed' 
                : 'bg-pink-600 text-white hover:bg-pink-700'
            }`}
          >
            Previous
          </button>
          <span className="text-sm font-bold text-purple-900">
            Page {currentPage + 1} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => prev + 1)}
            disabled={(currentPage + 1) * weeksPerPage >= chartData.length}
            className={`px-4 py-1 rounded-md text-sm ${
              (currentPage + 1) * weeksPerPage >= chartData.length
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-pink-600 text-white hover:bg-pink-700'
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-pink-600"></div>
        </div>
      ) : chartData.length > 0 ? (
        <div className="w-full overflow-x-auto">
          <div className="min-w-[800px]">
            <ResponsiveContainer width="100%" height={450}>
              <BarChart data={paginatedData} margin={{ top: 40, right: 30, left: 20, bottom: 60 }}>
                <defs>
                  <linearGradient id="brGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.9} />
                    <stop offset="50%" stopColor="#6366F1" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.9} />
                  </linearGradient>
                </defs>

                <XAxis
                  dataKey="week"
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  tick={{ fontSize: 12 }}
                  label={{
                    value: 'Week Number',
                    position: 'insideBottom',
                    offset: -50,
                    fontSize: 14,
                    fontWeight: 'bold',
                  }}
                />
                <YAxis 
                  label={{
                    value: 'BR Count',
                    angle: -90,
                    position: 'insideLeft',
                    fontSize: 14,
                    fontWeight: 'bold',
                  }}
                  />
                <Tooltip 
                  content={({ active, payload }) => {
                    if (active && payload?.length) {
                      const { startDate, endDate, brValue } = payload[0].payload;
                      return (
                        <div className="bg-white p-3 border border-gray-300 rounded-lg shadow-lg">
                          <p className="font-bold text-gray-800">Week Details</p>
                          <p className="text-sm text-gray-600">{startDate} to {endDate}</p>
                          <p className="mt-1 text-blue-600 font-semibold">BR Count: {brValue}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend verticalAlign="top" align="center" />
                <Bar
                  dataKey="brValue"
                  name="BR Count Weekly"
                  fill="url(#brGradient)"
                  radius={[6, 6, 0, 0]}
                  barSize={dynamicBarSize}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center h-64">
          <p className="text-gray-500 text-lg">No data available for the selected filters</p>
        </div>
      )}
    </div>
  );
};

export default StatusSummaryChart;